/**
 * tr-toast — notifications globales de Traction (P1-06, 15·R3/R11).
 *
 * Rendu UNE fois dans le layout (snippets/tr-toast.liquid). Bus :
 *   document.dispatchEvent(new CustomEvent('tr:toast', { detail: { message, type, duration } }))
 *
 * Ce que FullStack n'a pas (15·R3) :
 * - `role="status" aria-live="polite"` : chaque message est annoncé aux lecteurs d'écran ;
 * - file d'attente : un nouveau message n'écrase jamais le précédent, il attend ;
 * - `textContent` (jamais d'innerHTML) ;
 * - pause au survol ET au focus (bouton fermer focusable) ;
 * - zéro chaîne en dur : tous les messages viennent de l'appelant ou des data-* (locales).
 *
 * Confort intégré : après un ajout panier (`tr:cart-add`) sans tiroir ouvert,
 * un toast de confirmation s'affiche (data-string-added).
 */

import { TR_EVENTS } from './tr-events.js';

const SHOW = 'tr-toast--visible';
const DEFAULT_DURATION = 4000;
const ERROR_DURATION = 6000;

class TrToast extends HTMLElement {
  #controller = null;
  #queue = [];
  #timer = 0;
  #shownAt = 0;
  #remaining = 0;
  #busy = false;

  connectedCallback() {
    /* Singleton : un seul hôte actif par page. */
    const others = document.querySelectorAll('tr-toast');
    if (others[0] !== this) {
      this.remove();
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener(TR_EVENTS.TOAST, this.#onToast, { signal });
    document.addEventListener(TR_EVENTS.CART_ADD, this.#onCartAdd, { signal });

    this.addEventListener('mouseenter', this.#pause, { signal });
    this.addEventListener('mouseleave', this.#resume, { signal });
    this.addEventListener('focusin', this.#pause, { signal });
    this.addEventListener('focusout', this.#resume, { signal });
    this.addEventListener('click', this.#onClick, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    window.clearTimeout(this.#timer);
  }

  /* ---------- public ---------- */

  /**
   * @param {string} message — texte déjà localisé.
   * @param {{type?:('success'|'error'|'info'), duration?:number}} [options]
   */
  show(message, { type = 'info', duration } = {}) {
    if (typeof message !== 'string' || message.trim() === '') return;

    this.#queue.push({
      message,
      type,
      duration: Number(duration) || (type === 'error' ? ERROR_DURATION : DEFAULT_DURATION),
    });

    if (this.#busy === false) this.#next();
  }

  /* ---------- interne ---------- */

  #onToast = (event) => {
    const detail = event.detail ?? {};
    this.show(detail.message, { type: detail.type, duration: detail.duration });
  };

  /** Confirmation d'ajout quand le tiroir ne s'ouvre pas (sinon il EST le retour). */
  #onCartAdd = () => {
    window.requestAnimationFrame(() => {
      const drawer = document.querySelector('tr-cart-drawer');
      if (drawer !== null && drawer.isOpen === true) return;
      if (document.body.classList.contains('overflow-hidden') && drawer !== null) return;

      this.show(this.dataset.stringAdded ?? '', { type: 'success' });
    });
  };

  #onClick = (event) => {
    if (event.target.closest('[data-ref="toastClose"]') === null) return;
    this.#hide();
  };

  #next() {
    const item = this.#queue.shift();
    if (item === undefined) {
      this.#busy = false;
      return;
    }

    this.#busy = true;

    const body = this.querySelector('[data-ref="toastMessage"]');
    if (body !== null) {
      /* Relecture forcée même à texte identique (aria-live). */
      body.textContent = '';
      window.requestAnimationFrame(() => {
        body.textContent = item.message;
      });
    }

    this.dataset.type = item.type;
    this.classList.add(SHOW);

    this.#remaining = item.duration;
    this.#shownAt = Date.now();
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#hide(), item.duration);
  }

  #hide() {
    window.clearTimeout(this.#timer);
    this.classList.remove(SHOW);

    /* Laisse la transition se terminer avant le message suivant. */
    this.#timer = window.setTimeout(() => this.#next(), 250);
  }

  #pause = () => {
    if (this.#busy === false) return;
    window.clearTimeout(this.#timer);
    this.#remaining = Math.max(this.#remaining - (Date.now() - this.#shownAt), 1000);
  };

  #resume = (event) => {
    if (this.#busy === false) return;
    if (event.type === 'focusout' && this.contains(event.relatedTarget)) return;

    this.#shownAt = Date.now();
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#hide(), this.#remaining);
  };
}

if (customElements.get('tr-toast') === undefined) {
  customElements.define('tr-toast', TrToast);
}
